import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useSnackbar } from "notistack";
import Icon from "../../../../components/HOC/Icon";
import useLoadLocalStorage from "../../../../hooks/useLoadLocalStorage";
import {
  COPIED_PASSENGERS_STORAGE_KEY,
  removeCopiedPassengerFromStorage,
} from "../../../../utils/booking/copiedPassengersStorage";
import CommonFreeTextInput from "../../../../components/common/inputs/CommonFreeTextInput";
// import CommonDatePicker from "../../../../components/common/inputs/CommonDatePicker";
import TextInput from "../../../../components/form/TextInput";
import CommonCountryInput from "../../../../components/common/inputs/CommonCountryInput";
import CommonDropdownInput from "../../../../components/common/inputs/CommonDropdownInput";
import FetchTravelers from "../../../../components/form/FetchTravelers";
import { travelersInfo } from "../../../../data/flight/travelersInfo";
import { clone } from "../../../../features/utils/objClone";
import { getPassengerCategory } from "../../../../utils/getPassengerCategory";
import getSavedTravelers from "../../../../controllers/Travelers/getSavedTravelers";
import { isCustomerSite } from "../../../../utils/getsubDomain";
import { SSR } from "../../../../data/flight/booking/SSR";
import { validateAge } from "../../../../components/flight/PrimaryPassenger";
import BookPassengerDocumentScan, {
  mapOcrTravelerDocument,
} from "./BookPassengerDocumentScan";
import {
  sanitizePassengerName,
  splitGivenNames,
  normalizePassengerNames,
} from "../../../../utils/booking/sanitizePassengerName";

const ADULT_TITLE_OPTIONS = [
  { code: "Mr", label: "Mr" },
  { code: "Ms", label: "Ms" },
  { code: "Mrs", label: "Mrs" },
];

const MINOR_TITLE_OPTIONS = [
  { code: "Miss", label: "Miss" },
  { code: "Mstr", label: "Master" },
];

function isMinorPassengerType(type) {
  const normalized = String(type || "").toLowerCase();
  return normalized === "child" || normalized === "infant";
}

function getTitleOptions(type) {
  return isMinorPassengerType(type) ? MINOR_TITLE_OPTIONS : ADULT_TITLE_OPTIONS;
}

export function genderFromTitle(title) {
  const normalized = String(title || "").trim();
  if (!normalized) return "";
  if (normalized === "Mr" || normalized === "Mstr") return "Male";
  if (["Ms", "Mrs", "Miss"].includes(normalized)) return "Female";
  return "";
}

function inferTitleFromGender(gender, type) {
  const normalizedGender = normalizeGender(gender);
  if (!normalizedGender) return "";

  if (isMinorPassengerType(type)) {
    return normalizedGender === "Male" ? "Mstr" : "Miss";
  }

  return normalizedGender === "Male" ? "Mr" : "Ms";
}

function normalizeTitle(title, gender, type) {
  const options = getTitleOptions(type);
  const normalizedTitle = String(title || "").trim();
  if (options.some((option) => option.code === normalizedTitle)) {
    return normalizedTitle;
  }
  return inferTitleFromGender(gender, type);
}
function normalizeGender(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (
    normalized === "male" ||
    normalized === "m" ||
    normalized === "mr" ||
    normalized === "mstr" ||
    normalized === "master"
  ) {
    return "Male";
  }
  if (
    normalized === "female" ||
    normalized === "f" ||
    normalized === "ms" ||
    normalized === "mrs" ||
    normalized === "miss"
  ) {
    return "Female";
  }
  if (value === "Male" || value === "Female") return value;
  return "";
}

function parseDate(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : null;
}

function convertPassengerType(name) {
  if (name === "adult") return "ADT";
  if (name === "child") return "CNN";
  if (name === "infant") return "INF";
  return name;
}

function mergePassengerData(source = {}, passengerType) {
  const gender = normalizeGender(source.gender);
  const title = normalizeTitle(source.title, gender, passengerType);

  return {
    ...clone(travelersInfo),
    ...source,
    title,
    gender: genderFromTitle(title) || gender,
    document: {
      ...travelersInfo.document,
      ...(source.document || {}),
    },
  };
}

export function buildPassengerPayload(obj, passengerType) {
  const normalizedObj = mergePassengerData(obj, passengerType);
  const named = normalizePassengerNames(normalizedObj);
  const birthDate = named.birthDate
    ? moment(named.birthDate).format("YYYY-MM-DD")
    : "";
  const document = { ...named.document };

  if (document.expiryDate) {
    document.expiryDate = moment(document.expiryDate).format("YYYY-MM-DD");
  }

  return {
    ...named,
    birthDate,
    document,
    expiryDate: document.expiryDate,
    gender: genderFromTitle(named.title) || named.gender,
    gotType: passengerType,
    type: convertPassengerType(passengerType),
  };
}

export default function BookPassengerForm({
  data: passengerData,
  type,
  onChange,
  onContactChange,
  hide,
}) {
  const data = mergePassengerData(passengerData, type);
  const titleOptions = useMemo(() => getTitleOptions(type), [type]);
  const [savedTravelers, setSavedTravelers] = useState([]);
  const [copiedPassengers, setCopiedPassengers] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const onChangeRef = useRef(onChange);
  const customer = isCustomerSite();
  const hasCopiedPassenger = copiedPassengers.length > 0;

  useLoadLocalStorage(COPIED_PASSENGERS_STORAGE_KEY, (data) => {
    setCopiedPassengers(Array.isArray(data) ? data : []);
  });

  const availableTravelers = useMemo(() => {
    const matchesPassengerType = (traveler) =>
      type?.toLowerCase() ===
      getPassengerCategory(traveler?.birthDate)?.toLowerCase();

    const copied = (copiedPassengers || []).filter(matchesPassengerType);
    const saved = (savedTravelers || []).filter(matchesPassengerType);

    const seen = new Set();
    return [...copied, ...saved].filter((traveler) => {
      const key = [
        traveler?.lastName,
        traveler?.firstName,
        traveler?.birthDate,
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .join("|");

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [copiedPassengers, savedTravelers, type]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!customer) loadTravelers();
  }, []);

  async function loadTravelers() {
    const res = await getSavedTravelers();
    if (res.return) {
      setSavedTravelers(
        res?.data?.data?.filter(
          (obj) =>
            type?.toLowerCase() ===
            getPassengerCategory(obj?.birthDate)?.toLowerCase(),
        ),
      );
    }
  }

  function updatePassenger(nextData) {
    onChangeRef.current?.(buildPassengerPayload(nextData, type));
  }

  function handleData(obj) {
    updatePassenger(mergePassengerData(obj, type));
  }

  function handleNameField(key, value) {
    const sanitizedValue = sanitizePassengerName(value);
    const next = { ...data, [key]: sanitizedValue };

    // Keep first/middle separate if user pastes a full given name into firstName
    if (key === "firstName" || key === "middleName") {
      const { firstName, middleName } = splitGivenNames(
        key === "firstName" ? value : next.firstName,
        key === "middleName" ? value : next.middleName,
      );
      next.firstName = firstName;
      next.middleName = middleName;
    }

    handleData(next);
  }

  function handleTitleChange(title) {
    handleData({
      ...data,
      title,
      gender: genderFromTitle(title),
    });
  }

  function handleTravelerSelect(id) {
    let travelerData = null;
    if (typeof id === "object") travelerData = id;

    const {
      _id: _,
      account,
      createdAt,
      updatedAt,
      document,
      ...rest
    } = travelerData || availableTravelers?.find((obj) => obj?._id === id) || {};

    if (document) {
      document.holder = document?.holder === "true" ? true : false;
    }

    if (rest || document) {
      updatePassenger(
        mergePassengerData({
          ...rest,
          gender: normalizeGender(rest.gender),
          document,
        }, type),
      );
    }
  }

  function handleOcrExtracted(ocrData) {
    const mappedData = mapOcrTravelerDocument(
      ocrData,
      mergePassengerData(passengerData, type),
    );
    updatePassenger(mappedData);
  }

  function handlePasteCopiedPassenger() {
    const passenger = copiedPassengers[0];
    if (!passenger) return;

    handleTravelerSelect(passenger);
    onContactChange?.({
      email: passenger.email,
      phone: passenger.phone,
    });
    removeCopiedPassengerFromStorage(passenger._id);
    enqueueSnackbar("Saved passenger pasted.", { variant: "success" });
  }

  const birthDateError =
    data.birthDate && !validateAge(data.birthDate, type)[0];
  const expiredPassport =
    data?.document?.expiryDate &&
    moment(data.document.expiryDate).isBefore(moment(), "day");

  return (
    <div className="flex flex-col gap-5">
      {!customer ? (
        <FetchTravelers
          from={availableTravelers}
          shrink={false}
          label="/ Select an existing passenger"
          onChange={(obj) => handleTravelerSelect(obj)}
        />
      ) : null}

      <div className="flex flex-wrap items-start gap-2">
        <BookPassengerDocumentScan onExtracted={handleOcrExtracted} />
        {hasCopiedPassenger ? (
          <button
            type="button"
            onClick={handlePasteCopiedPassenger}
            className="inline-flex items-center gap-2 rounded-lg border border-[#DBDBDB] bg-white px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 transition-colors w-fit"
          >
            <Icon icon="mdi:content-paste" className="w-5 h-5" />
            <span>Paste saved passenger</span>
          </button>
        ) : null}
      </div>

      <div className="max-w-xs">
        <CommonDropdownInput
          label="Title"
          value={data.title || ""}
          onChange={handleTitleChange}
          options={titleOptions}
          optionObject
          placeholder="Select title"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CommonFreeTextInput
          label="First name"
          placeholder="e.g. John"
          value={data.firstName || ""}
          onChange={(val) => handleNameField("firstName", val)}
        />
        <CommonFreeTextInput
          label="Middle name"
          placeholder="e.g. Michael"
          value={data.middleName || ""}
          onChange={(val) => handleNameField("middleName", val)}
        />
        <CommonFreeTextInput
          label="Surname"
          placeholder="e.g. Ike"
          value={data.lastName || ""}
          onChange={(val) => handleNameField("lastName", val)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CommonCountryInput
          label="Nationality"
          value={data?.document?.nationality || ""}
          onChange={(val) =>
            handleData({
              ...data,
              document: { ...data.document, nationality: val },
            })
          }
          placeholder="Select nationality"
          dropdownPosition="bottom-20"
        />
        <div className="md:mt-6">
          <TextInput
            type="date"
            label="Date of Birth"
            required
            error={birthDateError}
            helperText={
              birthDateError ? (
                <span className="text-red-500">
                  {validateAge(data.birthDate, type)[1]}
                </span>
              ) : null
            }
            value={data.birthDate || ""}
            onChange={(ev) =>
              handleData({ ...data, birthDate: ev.target.value })
            }
          />
        </div>
        {/* <CommonDatePicker
          label="Date of birth"
          placeholder="Select date of birth"
          maxDate={new Date()}
          value={parseDate(data.birthDate)}
          error={birthDateError}
          onChange={(date) =>
            handleData({
              ...data,
              birthDate: date ? moment(date).format("YYYY-MM-DD") : "",
            })
          }
        /> */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CommonFreeTextInput
          label="Passport or ID number"
          placeholder="e.g. A1234567"
          value={data?.document?.number || ""}
          onChange={(val) =>
            handleData({
              ...data,
              document: { ...data.document, number: val },
            })
          }
        />
        <div className="md:mt-6">
          <TextInput
            type="date"
            label="Passport or ID Expiration Date"
            required
            error={expiredPassport}
            helperText={
              expiredPassport ? (
                <span className="text-red-500">Expired Passport!</span>
              ) : null
            }
            value={data?.document?.expiryDate || ""}
            onChange={(ev) =>
              handleData({
                ...data,
                document: { ...data.document, expiryDate: ev.target.value },
              })
            }
          />
        </div>
        {/* <CommonDatePicker
          label="Passport or ID expiration date"
          placeholder="Select expiration date"
          minDate={new Date()}
          value={parseDate(data?.document?.expiryDate)}
          error={expiredPassport}
          onChange={(date) =>
            handleData({
              ...data,
              document: {
                ...data.document,
                expiryDate: date ? moment(date).format("YYYY-MM-DD") : "",
              },
            })
          }
          calenderPosition="-top-10"
        /> */}
      </div>

      {!hide?.includes("additional detail") ? (
        <div className="flex flex-col gap-4">
          <h5>Additional details</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonFreeTextInput
              label="Frequent flyer number"
              placeholder="Enter here"
              value={data?.frequentFlyer || ""}
              onChange={(val) => handleData({ ...data, frequentFlyer: val })}
            />
            <CommonDropdownInput
              label="Special assistance"
              placeholder="Select"
              value={data?.specialAssistance || ""}
              onChange={(val) =>
                handleData({ ...data, specialAssistance: val })
              }
              options={Object.entries(SSR).map(([code, label]) => ({
                code,
                label,
              }))}
              optionObject
              dropdownPosition="bottom-20"
            />
          </div>
        </div>
      ) : null}

      {!hide?.includes("remark") ? (
        <div className="flex flex-col gap-4">
          <h5>Remarks</h5>
          <CommonFreeTextInput
            label=""
            placeholder="Add any special requirements here"
            value={data?.generalRemark || ""}
            onChange={(val) => handleData({ ...data, generalRemark: val })}
          />
        </div>
      ) : null}
    </div>
  );
}
